// Transport for the public "tasks assigned to X" view: the anonymous page never
// touches AppSync (every table is userPool-authed) -- it fetches the
// share-assigned-tasks Lambda's Function URL, which validates the unguessable
// share id and returns a whitelisted {itemName, assignedEmail, tasks}. Same
// outputs pattern as the other two share transports: the URL is baked into
// amplify_outputs.json per branch, read at call time so tests can swap it, and
// absent until that backend is deployed -- shareAssignedTasksAvailable() gates
// the owner-side Share button (and every share query) off in that case, so the
// frontend can ship first.

import outputs from '../amplify_outputs.json';

function shareAssignedTasksBase() {
  return outputs?.custom?.shareAssignedTasksFunctionUrl || '';
}

export function shareAssignedTasksAvailable() {
  return Boolean(shareAssignedTasksBase());
}

// Resolves to {status: 'ok', itemName, assignedEmail, editsAllowed, tasks,
// photos, photosOk} or {status: 'gone'} (revoked, never issued, or the item no
// longer exists -- the viewer can't tell and neither can we). An EMPTY `tasks`
// is a 200 and a real answer: the person has nothing open. Throws on network
// failures and server errors, which the page reports as "try again later"
// rather than "gone".
//
// RESHAPED, NOT PASSED THROUGH: a key the Lambda adds and this omits simply
// never reaches the page. `photosOk` defaults FALSE and `editsAllowed` defaults
// FALSE, which is what a response from a backend predating either feature reads
// as -- "couldn't ask" and "read-only", both the safe answer.
export async function fetchSharedAssignedTasks(shareId) {
  const base = shareAssignedTasksBase();
  if (!base) throw new Error('Task list sharing is not configured.');
  const query = new URLSearchParams({ shareId }).toString();
  const response = await fetch(`${base}?${query}`);
  if (response.status === 404 || response.status === 410) return { status: 'gone' };
  if (!response.ok) throw new Error(`Shared task list request returned ${response.status}`);
  const body = await response.json();
  return {
    status: 'ok',
    itemName: body?.itemName ?? '',
    assignedEmail: body?.assignedEmail ?? '',
    tasks: Array.isArray(body?.tasks) ? body.tasks : [],
    editsAllowed: body?.editsAllowed === true,
    photos: body?.photos && typeof body.photos === 'object' ? body.photos : {},
    photosOk: body?.photosOk === true,
  };
}
